import React, { type ChangeEvent } from 'react';

import { graphql, useFragment, useMutation } from 'react-relay';
import { Inline } from '@atlaskit/primitives/compiled';
import { Label } from '@atlaskit/form';
import Checkbox from '@atlaskit/checkbox';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';

import type { emailPrefDueDateNotificationMutation } from '@atlassian/jira-relay/src/__generated__/emailPrefDueDateNotificationMutation.graphql';
import type { emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification$key } from '@atlassian/jira-relay/src/__generated__/emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification.graphql';
import messages from '../../../ui/messages.tsx';

export const EmailPrefDueDateNotification = ({
	preferenceFragment,
}: {
	preferenceFragment: emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification$key;
}) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { showErrorFlag } = useShowErrorFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = useFragment(
		graphql`
			fragment emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification on JiraNotificationPreferences {
				dailyDueDateNotification {
					id @required(action: THROW)
					emailChannel {
						isEnabled
					}
				}
			}
		`,
		preferenceFragment,
	);

	const [commit] = useMutation<emailPrefDueDateNotificationMutation>(graphql`
		mutation emailPrefDueDateNotificationMutation($cloudId: ID!, $isEnabled: Boolean!) {
			jira {
				updateGlobalNotificationPreferences(
					cloudId: $cloudId
					input: {
						preferences: [
							{ channel: EMAIL, isEnabled: $isEnabled, type: DAILY_DUE_DATE_NOTIFICATION }
						]
					}
				) @optIn(to: "JiraDueDateNotificationsToggle") {
					success
					errors {
						message
					}
					preferences {
						...emailPrefDueDateNotif_personalSettingsDigestNotifications_EmailPrefDueDateNotification
					}
				}
			}
		}
	`);

	const [debouncedHandleToggleChange] = useDebouncedCallback(
		(e: ChangeEvent<HTMLInputElement>, analyticsEventUI: UIAnalyticsEvent) => {
			const newValue = e.currentTarget.checked;
			fireUIAnalytics(analyticsEventUI, 'toggleDueDateNotificationSwitch', {
				isToggleOn: newValue,
			});

			commit({
				variables: {
					cloudId,
					isEnabled: newValue,
				},
				optimisticResponse: {
					jira: {
						updateGlobalNotificationPreferences: {
							success: true,
							errors: [],
							preferences: {
								dailyDueDateNotification: {
									id: data.dailyDueDateNotification?.id ?? '',
									emailChannel: {
										isEnabled: newValue,
									},
								},
							},
						},
					},
				},
				onError: (error: Error) => {
					showErrorFlag();
					fireErrorAnalytics({
						meta: {
							id: 'mutateEmailPrefDueDateNotification',
							packageName: 'jiraPersonalSettingsNotificationsDigests',
							teamName: 'frontier',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
				onCompleted: (response) => {
					const errors = response?.jira?.updateGlobalNotificationPreferences?.errors;
					if (errors && errors.length > 0) {
						showErrorFlag();
						errors.forEach((error) => {
							fireErrorAnalytics({
								meta: {
									id: 'mutateEmailPrefDueDateNotificationCompletedError',
									packageName: 'jiraPersonalSettingsNotificationsDigests',
									teamName: 'frontier',
								},
								error: new Error(error?.message || 'Unknown Error'),
								sendToPrivacyUnsafeSplunk: true,
							});
						});
					} else {
						fireOperationalAnalytics(
							createAnalyticsEvent({}),
							'mutateEmailPrefDueDateNotification success',
						);
					}
				},
			});
		},
		300,
		{ leading: true, trailing: true },
	);

	return (
		<Label htmlFor="notify-via-email-checkbox">
			<Inline space="space.100">
				<Checkbox
					isChecked={!!data.dailyDueDateNotification?.emailChannel?.isEnabled}
					onChange={(e, analyticsEventUI) => debouncedHandleToggleChange(e, analyticsEventUI)}
					label={formatMessage(messages.digestsNotificationsReceiveNotificationsEmail)}
					testId="personal-settings-digest-notifications.common.ui.email-pref-due-date-notif.notify-via-email-checkbox"
				/>
			</Inline>
		</Label>
	);
};
